import { getItemIcon, ItemType, type ItemTypeValue, TILE_SIZE } from '@dreamgame/shared';
import { Container, Graphics, Sprite, Text, type Texture } from 'pixi.js';
import { getAllGroundItems } from '../../state/groundItems';
import { getGroundItemColor } from '../constants';
import { loadTexture, onCategoryReload } from '../TextureManager';

// ============================================================================
// ItemLayer - Renders ground items
// ============================================================================

let container: Container;
let graphics: Graphics;

const itemTextures: Map<ItemTypeValue, Texture> = new Map();
// PERFORMANCE: Use integer keys (networkId) instead of string template keys
const itemSprites: Map<number, Sprite> = new Map();
const quantityTexts: Map<number, Text> = new Map();
const _usedSpriteKeys: Set<number> = new Set();
const _usedTextKeys: Set<number> = new Set();

export const createItemLayer = (): { container: Container; graphics: Graphics } => {
  container = new Container();
  graphics = new Graphics();
  return { container, graphics };
};

export const loadItemTextures = async (): Promise<void> => {
  try {
    for (const key of Object.keys(ItemType)) {
      const itemType = ItemType[key as keyof typeof ItemType];
      if (itemType === ItemType.NONE) {
        continue;
      }
      const iconFile = getItemIcon({ itemType });
      if (iconFile) {
        try {
          const texture = await loadTexture({ path: `/items/${iconFile}` });
          itemTextures.set(itemType, texture);
        } catch {
          // Texture file not found, will use fallback
        }
      }
    }
    console.log(`Loaded ${itemTextures.size} item textures`);
  } catch (error) {
    console.warn('Failed to load item textures, using fallback colors', error);
  }
};

// Reload textures (clears sprites and reloads from TextureManager)
export const reloadItemTextures = async (): Promise<void> => {
  // Clear all existing sprites
  for (const sprite of itemSprites.values()) {
    sprite.destroy();
  }
  itemSprites.clear();
  for (const text of quantityTexts.values()) {
    text.destroy();
  }
  quantityTexts.clear();

  // Clear texture cache
  itemTextures.clear();

  // Reload textures
  await loadItemTextures();
  console.log('Item textures reloaded');
};

// Register for texture reload events
onCategoryReload({ category: 'items', callback: reloadItemTextures });

const ITEM_SIZE = 16; // Size for the item sprite/shape

// Render or update item sprite with texture
const renderItemSprite = ({
  spriteKey,
  itemX,
  itemY,
  texture,
}: {
  spriteKey: number;
  itemX: number;
  itemY: number;
  texture: Texture;
}): void => {
  _usedSpriteKeys.add(spriteKey);

  const existingSprite = itemSprites.get(spriteKey);
  if (existingSprite) {
    if (existingSprite.texture !== texture) {
      existingSprite.texture = texture;
    }
    existingSprite.x = itemX;
    existingSprite.y = itemY;
    existingSprite.visible = true;
  } else {
    const sprite = new Sprite(texture);
    sprite.width = ITEM_SIZE;
    sprite.height = ITEM_SIZE;
    sprite.x = itemX;
    sprite.y = itemY;
    container.addChild(sprite);
    itemSprites.set(spriteKey, sprite);
  }
};

// Hide unused sprites and texts
const cleanupUnusedVisuals = (): void => {
  for (const [key, sprite] of itemSprites) {
    if (!_usedSpriteKeys.has(key)) {
      sprite.visible = false;
    }
  }

  for (const [key, text] of quantityTexts) {
    if (!_usedTextKeys.has(key)) {
      text.visible = false;
    }
  }
};

export const drawGroundItems = (): void => {
  graphics.clear();
  _usedSpriteKeys.clear();
  _usedTextKeys.clear();

  const groundItems = getAllGroundItems();

  for (const item of groundItems) {
    // Use tileX/tileY + 0.5 to center items on their tile
    const worldX = (item.tileX + 0.5) * TILE_SIZE;
    const worldY = (item.tileY + 0.5) * TILE_SIZE;
    const itemType = item.itemType as ItemTypeValue;
    // PERFORMANCE: Use integer networkId as key instead of string template
    const spriteKey = item.networkId;
    const itemX = worldX - ITEM_SIZE / 2;
    const itemY = worldY - ITEM_SIZE / 2;

    const texture = itemTextures.get(itemType);
    if (texture) {
      renderItemSprite({ spriteKey, itemX, itemY, texture });
    } else {
      drawItemFallback({ itemX, itemY, itemSize: ITEM_SIZE, itemType });
    }

    if (item.quantity > 1) {
      drawQuantityText({ worldX, worldY, quantity: item.quantity, spriteKey });
    }
  }

  cleanupUnusedVisuals();
};

const drawItemFallback = ({
  itemX,
  itemY,
  itemSize,
  itemType,
}: {
  itemX: number;
  itemY: number;
  itemSize: number;
  itemType: ItemTypeValue;
}): void => {
  // Shadow
  graphics.setFillStyle({ color: 0x000000, alpha: 0.3 });
  graphics.roundRect(itemX + 2, itemY + 2, itemSize, itemSize, 2);
  graphics.fill();

  // Item body
  const itemColor = getGroundItemColor({ itemType });
  graphics.setFillStyle({ color: itemColor });
  graphics.roundRect(itemX, itemY, itemSize, itemSize, 2);
  graphics.fill();

  // Border
  graphics.setStrokeStyle({ width: 1, color: 0xffffff, alpha: 0.3 });
  graphics.roundRect(itemX, itemY, itemSize, itemSize, 2);
  graphics.stroke();
};

const drawQuantityText = ({
  worldX,
  worldY,
  quantity,
  spriteKey,
}: {
  worldX: number;
  worldY: number;
  quantity: number;
  spriteKey: number;
}): void => {
  // PERFORMANCE: Use negative networkId as text key to avoid string allocation
  const textKey = -spriteKey;
  _usedTextKeys.add(textKey);

  const quantityStr = quantity > 99 ? '99+' : String(quantity);
  const textX = worldX + 6;
  const textY = worldY - 10;

  const existingText = quantityTexts.get(textKey);
  if (existingText) {
    existingText.text = quantityStr;
    existingText.x = textX;
    existingText.y = textY;
    existingText.visible = true;
  } else {
    const text = new Text({
      text: quantityStr,
      style: {
        fontFamily: 'monospace',
        fontSize: 8,
        fontWeight: 'bold',
        fill: 0xffffff,
        stroke: { color: 0x000000, width: 1.5 },
      },
      resolution: 2,
    });
    text.x = textX;
    text.y = textY;
    container.addChild(text);
    quantityTexts.set(textKey, text);
  }
};
